"use client";

import { useEffect, useState } from "react";

interface MorphingBlobProps {
  className?: string;
}

export default function MorphingBlob({ className = "" }: MorphingBlobProps) {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  /* eslint-disable react-hooks/set-state-in-effect -- syncing with external system (matchMedia) */
  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const userPref = document.documentElement.getAttribute("data-reduce-motion") === "true";
    setPrefersReducedMotion(mediaQuery.matches || userPref);

    const handleChange = (e: MediaQueryListEvent) => {
      const userPrefNow = document.documentElement.getAttribute("data-reduce-motion") === "true";
      setPrefersReducedMotion(e.matches || userPrefNow);
    };
    mediaQuery.addEventListener("change", handleChange);

    const observer = new MutationObserver(() => {
      const userPrefNow = document.documentElement.getAttribute("data-reduce-motion") === "true";
      setPrefersReducedMotion(mediaQuery.matches || userPrefNow);
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["data-reduce-motion"] });

    return () => {
      mediaQuery.removeEventListener("change", handleChange);
      observer.disconnect();
    };
  }, []);
  /* eslint-enable react-hooks/set-state-in-effect */

  if (prefersReducedMotion) {
    return null;
  }

  return (
    <div className={`absolute inset-0 overflow-hidden pointer-events-none contain-layout ${className}`}>
      <svg
        viewBox="0 0 800 600"
        width="800"
        height="600"
        className="absolute w-[140%] h-[140%] -top-[20%] -left-[20%] opacity-30"
        preserveAspectRatio="xMidYMid slice"
        aria-hidden="true"
      >
        <defs>
          <linearGradient id="blob-gradient-1" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="var(--gold)" stopOpacity="0.6" />
            <stop offset="50%" stopColor="var(--pink)" stopOpacity="0.4" />
            <stop offset="100%" stopColor="var(--purple)" stopOpacity="0.3" />
          </linearGradient>
          <linearGradient id="blob-gradient-2" x1="100%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="var(--pink)" stopOpacity="0.5" />
            <stop offset="100%" stopColor="var(--gold)" stopOpacity="0.3" />
          </linearGradient>
          <filter id="blob-blur">
            <feGaussianBlur in="SourceGraphic" stdDeviation="20" />
          </filter>
        </defs>
        
        <g filter="url(#blob-blur)">
          <path
            fill="url(#blob-gradient-1)"
            className="animate-blob-1"
          >
            <animate
              attributeName="d"
              dur="10s"
              repeatCount="indefinite"
              values="
                M400,100 C550,100 650,200 650,300 C650,400 550,500 400,500 C250,500 150,400 150,300 C150,200 250,100 400,100 Z;
                M420,80 C580,120 680,220 660,320 C640,420 520,520 380,480 C240,440 140,380 160,280 C180,180 260,40 420,80 Z;
                M380,120 C520,80 660,180 640,300 C620,420 500,540 360,500 C220,460 120,360 160,260 C200,160 240,160 380,120 Z;
                M400,100 C550,100 650,200 650,300 C650,400 550,500 400,500 C250,500 150,400 150,300 C150,200 250,100 400,100 Z
              "
            />
          </path>
          
          <path
            fill="url(#blob-gradient-2)"
            className="animate-blob-2"
          >
            <animate
              attributeName="d"
              dur="12s"
              repeatCount="indefinite"
              values="
                M200,150 C300,100 450,150 500,250 C550,350 500,450 400,480 C300,510 150,450 120,350 C90,250 100,200 200,150 Z;
                M220,130 C340,80 480,140 520,260 C560,380 480,480 360,500 C240,520 120,440 100,320 C80,200 100,180 220,130 Z;
                M180,170 C280,120 420,160 480,270 C540,380 520,470 420,490 C320,510 160,460 130,360 C100,260 80,220 180,170 Z;
                M200,150 C300,100 450,150 500,250 C550,350 500,450 400,480 C300,510 150,450 120,350 C90,250 100,200 200,150 Z
              "
            />
          </path>
          
          <ellipse
            cx="600"
            cy="150"
            fill="var(--gold)"
            fillOpacity="0.2"
            className="animate-blob-3"
          >
            <animate
              attributeName="rx"
              dur="8s"
              repeatCount="indefinite"
              values="80;120;90;80"
            />
            <animate
              attributeName="ry"
              dur="8s"
              repeatCount="indefinite"
              values="60;80;70;60"
            />
            <animate
              attributeName="cx"
              dur="10s"
              repeatCount="indefinite"
              values="600;650;580;600"
            />
            <animate
              attributeName="cy"
              dur="35s"
              repeatCount="indefinite"
              values="150;180;130;150"
            />
          </ellipse>
          
          <ellipse
            cx="150"
            cy="450"
            fill="var(--pink)"
            fillOpacity="0.25"
            className="animate-blob-4"
          >
            <animate
              attributeName="rx"
              dur="10s"
              repeatCount="indefinite"
              values="70;100;80;70"
            />
            <animate
              attributeName="ry"
              dur="10s"
              repeatCount="indefinite"
              values="50;70;55;50"
            />
            <animate
              attributeName="cx"
              dur="12s"
              repeatCount="indefinite"
              values="150;200;130;150"
            />
            <animate
              attributeName="cy"
              dur="40s"
              repeatCount="indefinite"
              values="450;420;470;450"
            />
          </ellipse>
        </g>
      </svg>
    </div>
  );
}
